/**
 * main.ts
 * Entry point. Boots the game once the DOM is ready.
 */

import { Game } from './Game';

declare global {
  interface Window {
    __futureBeaconBooted?: boolean;
    __futureBeaconRemoveLoader?: () => void;
    __futureBeaconShowLoaderMessage?: (text: string, isError?: boolean) => void;
    __futureBeaconMapSizePending?: boolean;
    __futureBeaconMapSizeReady?: () => void;
  }
}

const removeLoader = () => {
  if (window.__futureBeaconRemoveLoader) {
    window.__futureBeaconRemoveLoader();
    return;
  }
  const el = document.getElementById('fb-loader');
  if (el) el.remove();
  window.__futureBeaconBooted = true;
};

const boot = async () => {
  performance.mark('fb:boot-start');

  if (window.__futureBeaconMapSizePending) {
    await new Promise<void>(resolve => { window.__futureBeaconMapSizeReady = resolve; });
  }
  performance.mark('fb:map-size-selected');

  const slowBootTimer = window.setTimeout(() => {
    window.__futureBeaconShowLoaderMessage?.('Still tuning the signal...');
  }, 7000);

  try {
    performance.mark('fb:game-new');
    const game = new Game();

    // Expose for debugging in the browser console.
    (window as unknown as { game: Game }).game = game;

    // Await full initialisation (includes the Supabase content fetch and map
    // generation) before removing the loader so there is no blank-canvas gap.
    // The extra rAF tick lets Pixi paint its first frame before the overlay lifts.
    await game.start();
    performance.mark('fb:game-ready');
    performance.measure('fb:map-size-wait',  'fb:boot-start',       'fb:map-size-selected');
    performance.measure('fb:game-construct', 'fb:map-size-selected','fb:game-new');
    performance.measure('fb:game-start',     'fb:game-new',         'fb:game-ready');
    performance.measure('fb:total-boot',     'fb:boot-start',       'fb:game-ready');
    console.table(
      performance.getEntriesByType('measure')
        .filter(e => e.name.startsWith('fb:'))
        .map(e => ({ name: e.name, ms: Math.round(e.duration) })),
    );

    window.clearTimeout(slowBootTimer);
    requestAnimationFrame(removeLoader);
  } catch (error) {
    window.clearTimeout(slowBootTimer);
    console.error('Future Beacon boot failed', error);
    window.__futureBeaconShowLoaderMessage?.('Signal jammed. Reload to try again.', true);
    window.setTimeout(removeLoader, 1800);
  }
};

if (document.readyState === 'loading') {
  window.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
